import { z } from 'zod/v4';

import { animationSchemaLegacy, buttonHorizontalPositionSchema } from '@sites/entity-core';
import { THEME_COLORS_VARIABLES } from '@sites/site-theme';

/**
 * Content width as a percentage of the column. Mirrors what the editor's position panel offers
 * for a default-placement text widget; values above 100 make the host drop the CSS variable
 * entirely (see `textWidthCss`), so the AI-facing surface caps at 100 rather than exposing that
 * quirk as a way to "unset" the width.
 */
const WIDTH_MIN = 1;
const WIDTH_MAX = 100;

/**
 * How a text widget follows the site theme — both channels live INSIDE this HTML string, because
 * the widget has no colour or font field of its own. Stated here rather than in the drawer's
 * prompt for the usual reason: `apps/mcp-server` has no prompt, and an external agent reads only
 * the schema.
 *
 * - **Typography** rides on the tag. Both renderers wrap the content in `.sp-ui-text`, and the
 *   themed rules (`h1 { font-size: var(--h1-font-size); … }`, one per tag, in each app's global
 *   stylesheet) apply from there — so a bare `<h1>` is themed and a `<div style="font-size:36px">`
 *   is not.
 * - **Colour** rides on a CSS variable. This is exactly what the editor writes when a user picks a
 *   theme colour in the toolbar: TinyMCE's `forecolor` format runs `getVarColor`, which turns a
 *   pick matching the palette into `color: var(--primary-color)` and leaves anything else literal
 *   (`common/helpers/tiny-mce.ts`). A model that pastes the hex it saw in the snapshot produces
 *   text that looks right today and stops following the palette tomorrow — the failure is
 *   invisible until someone changes the theme.
 *
 * The variable names come from `THEME_COLORS_VARIABLES`, not from a second list here: the same
 * reasoning as the block's colour fields, where a hand-copied vocabulary was the thing that could
 * drift from the theme.
 */
const THEME_COLOR_VARIABLE_LIST = Object.values(THEME_COLORS_VARIABLES).join(', ');

/**
 * A text widget with an empty `content` renders as an invisible, unselectable gap — never what a
 * model actually means. Rejected loudly instead of silently creating one.
 */
export const textContentSchema = z
    .string()
    .min(1)
    .describe(
        'Rich-text HTML for the widget, e.g. "<h2>Our services</h2><p>What we do.</p>". Plain text is ' +
            'accepted but renders unstyled — prefer real HTML (h1-h6, p, strong, em, a, ul/li) so headings ' +
            'and paragraphs pick up the page theme (font, size, weight per tag). ' +
            'To colour text WITH the theme, reference the palette variable in an inline style — ' +
            `<span style="color: var(--primary-color)">…</span> — one of: ${THEME_COLOR_VARIABLE_LIST}. ` +
            'A hex literal freezes the colour: it stops following the palette even if it matches it today. ' +
            'Use a literal only for a colour that is deliberately outside the theme.'
    );

/**
 * THE text widget data shape as AI tools accept it — single source of truth shared by
 * `create_text_widget`/`update_text_widget` (and any equivalent), so their field surface can never
 * drift apart (same reasoning as `partial-button-item.schema.ts`). Lives here — not in a specific
 * app or the MCP server app — because both `apps/mcp-backend`'s mutations and
 * `apps/mcp-server`'s tools need the exact same shape, and neither may import the other (apps
 * depend on packages, never the reverse).
 *
 * Deliberately NOT entity-core's `textLegacyDefaultDataSchema.partial()`: that would drag in the
 * 15-field `position` grid as one nested object, the exact "one giant multi-group object" shape
 * models have repeatedly given up on. Instead the one position knob that matters for text —
 * content width — is flattened to a scalar `width`, and each caller's own mutation layer maps it
 * back onto `position.width` (the same flattening indent does for `height`/`mobileHeight`).
 *
 * `horizontalAlign` reuses the very schema entity-core's own text data uses (its name is
 * button-flavoured only because button declared the shared `'L' | 'M' | 'R'` enum first), so the
 * two can't diverge. `animation` is a single self-contained nested field reusing entity-core's
 * real sub-schema — made `.partial()` so a model toggling `isActive` isn't forced to resupply
 * type/position/speed it never touched.
 */
export const partialTextWidgetDataSchema = z.object({
    content: textContentSchema.optional(),
    horizontalAlign: buttonHorizontalPositionSchema
        .optional()
        .describe('Text block alignment inside the column: "L" (left), "M" (center) or "R" (right).'),
    width: z
        .number()
        .min(WIDTH_MIN)
        .max(WIDTH_MAX)
        .optional()
        .describe('Content width as a percentage of the column (1-100). Defaults to 100 on create.'),
    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether tablet/mobile styling stays synchronized with the desktop styling.'),
    animation: animationSchemaLegacy
        .partial()
        .optional()
        .describe(
            'Entrance animation. Partial — only the keys you provide change, e.g. ' +
                '{ "isActive": true, "animationType": "fade" }.'
        ),
});

export type PartialTextWidgetData = z.infer<typeof partialTextWidgetDataSchema>;
